import { useContext, useMemo } from 'react';
import { DateTime } from 'luxon';
import usePageType from './use-page-type';
import {
  bookingState,
  BookingStateCombinedState,
} from '../Pages/Booking/client-state/booking-state-context';
import {
  bookingEditState,
  BookingEditStateCombinedState,
} from '../Pages/BookingEdit/client-state/booking-edit-state-context';
import useBookingShop from '../Pages/Booking/server-data/use-booking-shop';
import useBookingEditShop from '../Pages/BookingEdit/server-data/use-booking-edit-shop';
import { Booking_GetShopQuery, BookingEdit_GetBookingQuery } from '../Utils/graphql-sdk';

type CancellationPolicy = {
  location?: number | null;
  boatCount?: number | null;
  duration?: number | null;
  date?: number | null;
  startTime?: number | null;
  customerInfo?: number | null;
  extrasAdd?: number | null;
  extrasRemove?: number | null;
  cancel?: number | null;
  refund?: number | null;
  voucherRefund?: number | null;
};

type warrantyHelpText = {
  lang?: string | null;
  value?: string | null;
};

type CancellationWarrantyOptions = {
  warrantyPurchaseCutoff?: number | null;
  warrantyHelpTextHeading?: Array<warrantyHelpText | null> | null;
  warrantyHelpTextBody?: Array<warrantyHelpText | null> | null;
};

type FullCancellationPolicy = {
  active?: boolean | null;
  default: CancellationPolicy;
  cancellationWarranty?: CancellationPolicy | null;
  options?: CancellationWarrantyOptions | null;
};

type ValidActions = {
  [key: string]: boolean | null;
};

const getActiveCancellationPolicy = (cancellationPolicies?: FullCancellationPolicy[]) => {
  if (cancellationPolicies) {
    for (const policy of cancellationPolicies) {
      if (policy.active) return policy;
    }
  }

  return null;
};

type LegacyWarrantyAddon = {
  itemId: string;
};

const hasLegacyCancellationWarranty = (addons: LegacyWarrantyAddon[]) => {
  if (addons?.length > 0) {
    const warranties = [
      '5f350201ef121610234be416',
      '6058a69b9f8e854a33aa786f',
      '606f013290b12a4e534d140e',
      '60db05992f31953135f3a45f',
    ];

    for (const addon of addons) {
      if (warranties.includes(addon.itemId)) return true;
    }
  }

  return false;
};

interface UseCancellationPolicyProps {
  referenceDate?: string | null;
}

const useCancellationPolicy = (props: UseCancellationPolicyProps) => {
  const pageType = usePageType();

  // This hook is currently only used on the booking and edit-booking pages
  const { data: bookingShop } = useBookingShop(false);
  const { data: bookingEditShop } = useBookingEditShop(false);
  const { state: stateBooking } = useContext(bookingState);
  const { state: stateBookingEdit } = useContext(bookingEditState);

  let shop:
    | Booking_GetShopQuery['getShop']
    | BookingEdit_GetBookingQuery['getBooking']['shop'];
  let state: BookingStateCombinedState['state'] | BookingEditStateCombinedState['state'];

  switch (pageType) {
    case 'booking': {
      shop = bookingShop;
      state = stateBooking;
      break;
    }
    case 'edit-booking': {
      shop = bookingEditShop;
      state = stateBookingEdit;
      break;
    }
    default: {
      state = stateBooking;
      break;
    }
  }

  const fullCancellationPolicy = useMemo(() => {
    const bookingCancellationPolicy = shop?.cancellationPolicies.find(
      (policy) => policy.id === state.booking.cancellationPolicyId
    );
    const activeCancellationPolicy = getActiveCancellationPolicy(
      shop?.cancellationPolicies
    );

    return bookingCancellationPolicy ?? activeCancellationPolicy;
  }, [shop?.cancellationPolicies, state.booking.cancellationPolicyId]);

  const cancellationPolicy = useMemo(() => {
    if (state.isWarrantyApplied || hasLegacyCancellationWarranty(state.order)) {
      return fullCancellationPolicy?.cancellationWarranty;
    }

    return fullCancellationPolicy?.default;
  }, [fullCancellationPolicy, state.isWarrantyApplied, state.order]);

  const hoursBeforeSailOut = useMemo(() => {
    if (props.referenceDate) {
      const sailOutTime = DateTime.fromISO(props.referenceDate);
      const now = DateTime.utc();

      const diff = sailOutTime.diff(now);
      const roundedDiffInHours = Math.floor(diff.as('hours'));

      return roundedDiffInHours;
    }

    return null;
  }, [props.referenceDate]);

  const validActions = useMemo(() => {
    if (cancellationPolicy && typeof hoursBeforeSailOut === 'number') {
      const actions: ValidActions = {};

      for (const key in cancellationPolicy) {
        if (key === 'id') continue;

        const policyKey = key as keyof typeof cancellationPolicy;

        if (
          cancellationPolicy[policyKey] === null ||
          cancellationPolicy[policyKey] === undefined
        ) {
          actions[key] = null;
          continue;
        }

        actions[key] = hoursBeforeSailOut >= (cancellationPolicy[policyKey] ?? 0);
      }

      return actions;
    }

    return {};
  }, [cancellationPolicy, hoursBeforeSailOut]);

  return { validActions, cancellationPolicy, fullCancellationPolicy, hoursBeforeSailOut };
};

export default useCancellationPolicy;
